<script lang="ts">
  import { getAttributeString, getStr } from '../../../../common/util';
  import Text from '../../../common/components/Text.svelte';
  import ExtraTextField from '../../../common/components/ExtraTextField.svelte';
  import Icon from '../../../common/components/Icon.svelte';
  import { productDetailMainData } from '../../store/pdp_store';

  export let data: any;
  let searchText = '';

  $: flatAttributes = Object.entries(data?.groupedfeatures ?? {}).flatMap(([attribute, attributeValues]) => {
    const normalizedAttributeValues = Array.isArray(attributeValues)
      ? attributeValues
      : attributeValues != null
        ? [attributeValues]
        : [];

    return normalizedAttributeValues.map(attributeValue => ({
      attribute,
      attributeValue: String(attributeValue ?? ''),
    }));
  });
  $: hasGroupedFeatures = flatAttributes.length > 0;
  $: normalizedSearchText = searchText.trim().toLowerCase();
  $: filteredAttributes = normalizedSearchText
    ? flatAttributes.filter(item => item.attributeValue.trim().toLowerCase().includes(normalizedSearchText))
    : flatAttributes;
  $: showNoResults = normalizedSearchText.length > 0 && filteredAttributes.length === 0;
</script>

{#if hasGroupedFeatures}
  <div class="product-attributes-container">
    <div class="product-key-and-specifications-header">
      <div class="head-attributes">
        {#if $productDetailMainData.isMarketPlaceProduct}
          <Text key="extra.marketplace.product.knowyourproduct.label" />
        {:else}
          <div class="product-key-model">
            <div class="model-number">
              <Text key="extra.pdp.specifications.model.number.message" args={[`${data?.modelNumber}`]} />
            </div>
            <div class="product-code">
              <Text key="extra.pdp.specifications.sku.message" args={[`${data?.productCode}`]} />
            </div>
          </div>
        {/if}
      </div>
      <div class="solo-paper">
        <!-- <Input bind:value={searchText} placeholder={getStr('extra.pdp.lookingToday')} class="solo-input" /> -->
        <ExtraTextField
          bind:value={searchText}
          placeholder={getStr('extra.pdp.specification.search.text')}
          className="solo-input"
          showBorder={false}
        />

        <Icon type="font" glyph="search" mobileSize={20} desktopSize={20} />
      </div>
    </div>
    <div class="product-attributes-details">
      {#if showNoResults}
        <div class="no-results">No results found</div>
      {:else}
        {#each filteredAttributes as item, i}
          <div class={`attribute-flex d-flex ${i % 2 === 1 ? 'alt-row' : ''}`}>
            <div class="attribute-text">
              {getAttributeString(':', item.attributeValue, 0)}
            </div>
            {#if getAttributeString(':', item.attributeValue, 1).trim() != 'null'}
              <div class="attribute-info">
                {getAttributeString(':', item.attributeValue, 1)}<span>i</span>
              </div>
            {/if}
          </div>
        {/each}
      {/if}
    </div>
  </div>
{/if}

<style lang="scss">
  @import '../../../common/styles/colors.new.scss';

  .product-attributes-container {
    .product-key-and-specifications-header {
      padding: 30.5px 20px;
      @include desktop-max-width {
        padding: 30px 16px 28px;
        background: $neutral-light-04;
        border-bottom: 1px solid $secondary-gray-02;
      }
      &-text {
        color: $neutral-dark-01;
        font-size: 20px;
        line-height: 22px;
        font-weight: 700;
        margin-bottom: 6px;
        @include desktop-min-width {
          font-size: 36px;
          line-height: 40px;
          margin-bottom: 12px;
        }
      }
      .head-attributes {
        color: $neutral-dark-02;
        font-size: 14px;
        line-height: 16px;
        font-weight: 400;
        margin-bottom: 15px;
        @include desktop-min-width {
          font-size: 18px;
          line-height: 20px;
          margin-bottom: 20px;
        }
        .product-key-model {
          display: flex;
          gap: 24px;
          :global(b) {
            font-weight: 500;
            color: $neutral-dark-01;
          }
          @include desktop-min-width {
            gap: 71px;
          }
        }
      }
    }
    .solo-paper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border: 1px solid $secondary-gray-02;
      border-radius: 8px;
      min-height: 48px;
      padding: 0 12px;
      background: $primary-white;
      :global(.solo-input) {
        width: 100%;
        border: none !important;
        outline: none;
        background: transparent;
        color: $neutral-dark-01;
        font-size: 14px;
        line-height: 16px;
      }
      :global(.solo-input::placeholder) {
        color: $neutral-dark-03;
      }
      :global(.c_icon_new) {
        color: $neutral-dark-03;
        flex-shrink: 0;
      }
    }
    .attribute-flex {
      font-size: 14px;
      line-height: 12px;
      padding: 16px 5px;
      flex-direction: row;
      align-items: center;
      height: 38px;
      @include desktop-min-width {
        height: 48px;
        font-size: 18px;
        line-height: 16px;
      }
      &.d-none {
        display: none;
      }
      &.alt-row {
        background: $color-alabaster-light;
      }
      .attribute-text {
        color: $neutral-dark-03;
        width: 50%;
        font-weight: 400;
        padding-right: 12px;
        text-align: left;
      }
      .attribute-info {
        width: 50%;
        text-align: left;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        color: $neutral-dark-01;
        @include desktop-min-width {
          font-weight: 500;
          font-size: 18px;
          line-height: 16px;
        }
        span {
          color: $icon-only-icon-blue;
          display: none;
          font-size: 14px;
          margin-left: 8px;
          border-radius: 50%;
          border: 1px solid $icon-only-icon-blue;
          height: 16px;
          width: 16px;
          background: $functional-yellow;
          align-items: center;
          justify-content: center;
        }
      }
    }
    .product-attributes-details {
      display: inline-block;
      width: 100%;
      padding: 1px 14px 42px;
      @include desktop-min-width {
        padding: 0px;
      }
    }
    .no-results {
      color: $neutral-dark-03;
      font-size: 14px;
      line-height: 18px;
      padding: 14px;
      @include desktop-min-width {
        padding: 0px;
      }
    }
  }
</style>
